// Stillhouse view — full-screen sign-in. Dark ink, grid + glow backdrop,
// mono labels, error pill on 401. Gated in App.tsx for unauthenticated users.
// Shows an outlined SSO button when the org has OIDC configured, and surfaces
// ?sso_error=<code> from a failed IdP round-trip as a readable banner.
import React, { useEffect, useState } from "react";
import { C, F, Btn, Pill, GridBG } from "../brand/apo";
import { StillhouseLogo } from "../brand/sh";
import { api, type SsoProvider } from "../api";
import { useAuth } from "../auth";

const labelStyle: React.CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  color: C.fgFaint,
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: 7,
  display: "block",
};
const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: C.surfaceHi,
  border: `1px solid ${C.line}`,
  color: C.fgDark,
  fontFamily: F.ui,
  fontSize: 13.5,
  padding: "10px 12px",
  outline: "none",
};

export function LoginView() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([]);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  // Read the SSO error code once on mount, then scrub it from the URL so it
  // doesn't resurface after a later successful sign-in. Only short-code-shaped
  // values are echoed back; anything else (a crafted link trying to inject
  // readable copy into the login screen) collapses to the generic banner,
  // signalled by the empty string.
  const [ssoError, setSsoError] = useState<string | null>(() => {
    const raw = new URLSearchParams(window.location.search).get("sso_error");
    if (raw === null) return null;
    return /^[a-z0-9_-]{1,40}$/i.test(raw) ? raw : "";
  });

  useEffect(() => {
    api
      .ssoProviders()
      .then((r) => setSsoProviders(r.providers))
      .catch(() => setSsoProviders([]));
    // Legacy SsoConfig on/off, so an org whose broker has zero registered
    // upstream providers (dev Dex passwordDB / legacy PUT /settings/sso) still
    // gets a generic SSO entry point instead of no affordance at all.
    api
      .ssoEnabled()
      .then((r) => setSsoEnabled(r.enabled))
      .catch(() => setSsoEnabled(false));
    if (new URLSearchParams(window.location.search).has("sso_error")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("sso_error");
      window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    }
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        // In-flow (not fixed) so the degraded-mode banner in App.tsx stays
        // visible above the sign-in card instead of being covered by it.
        position: "relative",
        flex: 1,
        minHeight: 0,
        width: "100%",
        background: C.ink,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: F.ui,
        overflow: "auto",
      }}
    >
      <GridBG opacity={0.35} size={40} />
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          top: "40%",
          transform: "translate(-50%,-50%)",
          width: 640,
          height: 640,
          borderRadius: "50%",
          background: `radial-gradient(closest-side, ${C.glow}12, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <StillhouseLogo wordmarkSize={28} iconSize={40} color={C.fgDark} />
        <div style={{ marginTop: 12, fontFamily: F.mono, fontSize: 10, letterSpacing: "0.26em", color: C.fgFaint, fontWeight: 600 }}>
          CONTROL PLANE · SIGN IN
        </div>

        <form
          onSubmit={submit}
          style={{
            marginTop: 30,
            width: 380,
            maxWidth: "calc(100vw - 48px)",
            background: C.surface,
            border: `1px solid ${C.line}`,
            boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
            padding: "26px 26px 24px",
          }}
        >
          {ssoError !== null && (
            <div
              style={{
                marginBottom: 18,
                padding: "10px 12px",
                background: `${C.rose}14`,
                border: `1px solid ${C.rose}44`,
                color: C.rose,
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              SSO sign-in failed{ssoError !== "" && (
                <React.Fragment>
                  {" "}
                  <span style={{ fontFamily: F.mono, fontSize: 11 }}>({ssoError})</span>
                </React.Fragment>
              )}. Try again, or sign in with your
              password.{" "}
              <button
                type="button"
                onClick={() => setSsoError(null)}
                aria-label="Dismiss"
                style={{ background: "transparent", border: "none", color: C.rose, cursor: "pointer", padding: 0, fontSize: 12.5, textDecoration: "underline" }}
              >
                Dismiss
              </button>
            </div>
          )}

          <label style={{ display: "block", marginBottom: 16 }}>
            <span style={labelStyle}>Email</span>
            <input
              style={inputStyle}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@complice.it"
              autoComplete="email"
              autoFocus
            />
          </label>
          <label style={{ display: "block", marginBottom: 18 }}>
            <span style={labelStyle}>Password</span>
            <input
              style={inputStyle}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>

          {error && (
            <div style={{ marginBottom: 16 }}>
              <Pill color={C.rose}>{error}</Pill>
            </div>
          )}

          <Btn primary type="submit" size="lg" style={{ width: "100%", opacity: submitting ? 0.7 : 1 }}>
            {submitting ? "Distilling session…" : "Sign in"}
          </Btn>

          {(ssoProviders.length > 0 || ssoEnabled) && (
            <React.Fragment>
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}>
                <span style={{ flex: 1, height: 1, background: C.line }} />
                <span style={{ fontFamily: F.mono, fontSize: 9.5, letterSpacing: "0.2em", color: C.fgFaint, fontWeight: 600 }}>OR</span>
                <span style={{ flex: 1, height: 1, background: C.line }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {ssoProviders.length > 0 ? (
                  ssoProviders.map((p) => (
                    <Btn
                      key={p.connectorId}
                      ghost
                      size="lg"
                      style={{ width: "100%" }}
                      onClick={() => {
                        window.location.href = `/api/auth/sso/start?provider=${encodeURIComponent(p.connectorId)}`;
                      }}
                    >
                      Sign in with {p.displayName}
                    </Btn>
                  ))
                ) : (
                  // No registered upstream providers, but SSO is enabled: land on
                  // the broker without a connector hint so Dex shows its own
                  // picker / passwordDB form.
                  <Btn
                    ghost
                    size="lg"
                    style={{ width: "100%" }}
                    onClick={() => {
                      window.location.href = `/api/auth/sso/start`;
                    }}
                  >
                    Sign in with SSO
                  </Btn>
                )}
              </div>
            </React.Fragment>
          )}
        </form>

        <div style={{ marginTop: 22, fontFamily: F.mono, fontSize: 10, letterSpacing: "0.18em", color: C.fgFaint }}>
          STILLHOUSE · CONTROL PLANE FOR MOONSHINE
        </div>
      </div>
    </div>
  );
}
